import { ReactElement } from "react";

import { BaseColumnProps } from "../BaseColumnProps";
import { ResolvedColumn } from "../ResolvedColumn";

/**
 * Resolves a table-unique instance key for each column.
 *
 * A column's key is derived from its explicit `columnKey` prop when provided, otherwise from
 * its React `key`, and finally from its `name`. When the same base key appears more than once
 * (e.g. the same data field rendered in multiple columns) each subsequent occurrence is suffixed
 * with an incrementing counter to guarantee uniqueness across the table.
 *
 * @param columns The columns to resolve keys for, in render order.
 * @returns The columns paired with their resolved unique keys.
 */
export function resolveColumnKeys(columns: ReadonlyArray<ReactElement<BaseColumnProps>>): ReadonlyArray<ResolvedColumn> {
  const bases = columns.map((element) => element.props.columnKey ?? (element.key != null ? String(element.key) : element.props.name));
  const reservedKeys = new Set(bases);
  const usedKeys = new Set<string>();
  return columns.map((element, index) => {
    const key = uniqueColumnKey(bases[index], usedKeys, reservedKeys);
    usedKeys.add(key);
    return { element, key };
  });
}

/*
  Produce a key not already present in `usedKeys`. Duplicate bases are suffixed with an
  incrementing `#` counter (`text`, `text#2`, `text#3`, ...). A generated suffix also skips
  any value reserved as another column's own base name, so a duplicate `text` does not steal
  the `text#2` key belonging to an explicitly named `text#2` column.
*/
function uniqueColumnKey(base: string, usedKeys: ReadonlySet<string>, reservedKeys: ReadonlySet<string>): string {
  if (!usedKeys.has(base)) return base;
  let occurrence = 2;
  let key = `${base}#${occurrence}`;
  while (usedKeys.has(key) || reservedKeys.has(key)) {
    occurrence += 1;
    key = `${base}#${occurrence}`;
  }
  return key;
}
